import { Button, Menu, Text } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import type { ReactNode } from 'react';

/**
 * One field's adopt actions, behind one small **Adopt ▾** — the drawer's field diff and the push
 * review both put it at the end of a differing field's row. Both actions take the environment's
 * value; they differ only in where it lands, which is what each item's second line says:
 *
 * - **Adopt as override**: the environment keeps its value as its own (`env == <name>`); master's
 *   default and every other environment stay as they are. Per-environment fields only.
 * - **Adopt to master**: the value becomes master's default, so every environment that does not
 *   override it inherits it — the caller opens a dry-run confirm before anything is written.
 *
 * Either may be absent; with neither there is nothing to render.
 */
export const AdoptFieldMenu = ({
    environment,
    label,
    onOverride,
    onMaster,
}: {
    environment: string;
    /** The field's label, for the accessible name. */
    label: string;
    onOverride?: () => void;
    onMaster?: () => void;
}) => {
    if (!onOverride && !onMaster) return null;
    const env = <span className="rgs-numeric">{environment}</span>;
    return (
        <Menu position="bottom-end" withinPortal shadow="md" width={280}>
            <Menu.Target>
                <Button
                    size="compact-xs"
                    variant="subtle"
                    rightSection={<IconChevronDown size={12} stroke={1.7} />}
                    aria-label={`Adopt ${environment}'s ${label}`}
                >
                    Adopt
                </Button>
            </Menu.Target>
            <Menu.Dropdown>
                <Menu.Label>
                    {label} from {env}
                </Menu.Label>
                {onOverride && (
                    <Item title="Adopt as override" onClick={onOverride}>
                        Only {env} keeps this value. Master's default and the other environments stay as they are.
                    </Item>
                )}
                {onMaster && (
                    <Item title="Adopt to master" onClick={onMaster}>
                        Becomes master's default for every environment. Shows what changes where first.
                    </Item>
                )}
            </Menu.Dropdown>
        </Menu>
    );
};

const Item = ({ title, onClick, children }: { title: string; onClick: () => void; children: ReactNode }) => (
    <Menu.Item onClick={onClick}>
        <Text fz="sm" fw={500}>
            {title}
        </Text>
        <Text fz="xs" c="dimmed">
            {children}
        </Text>
    </Menu.Item>
);
